import { useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'

import {
  GetExperimentVariantResponse,
  getExperimentVariant
} from '@/global/api/queries'
import { useAddError } from '@/global/hooks/useError'
import {
  Experiments,
  useExperimentStore
} from '@/global/store/useExperimentStore/useExperimentStore'
import { makeExperimentKey } from '@/global/utils/react-query-keys/react-query-keys'
import {
  getKeysFromLocalStorage,
  setKeysInLocalStorage
} from '@/global/utils/local-storage'
import {
  Context,
  ExperimentCode
} from '@/global/plugins/observability/observability.constants'

interface ConvertToStoreExperimentsStateBaseProps {
  experiments: Record<string, string> | Record<string, undefined>
  isReady: boolean
}

interface ConvertToReadyStoreExperimentsStateProps
  extends ConvertToStoreExperimentsStateBaseProps {
  experiments: Record<string, string>
  isReady: true
}

interface ConvertToUndefinedStoreExperimentsStateProps
  extends ConvertToStoreExperimentsStateBaseProps {
  experiments: Record<string, undefined>
  isReady: false
}

interface UseExperimentProps {
  userId?: string
  namespace: string
  keys: Array<string>
  allowedVariantsByKey?: Record<string, Array<string>>
}
/** 
  Get variant of an experiment and update the store with the result. 
  Fist, it checks if the keys are in the local storage, if they are, it updates the store with the values from the local storage. 
  Then, it fetches the remaining keys from the API and updates the store with the result.

  @param userId - The user id to identify the user.
  @param namespace - The namespace of the experiments.
  @param keys - The keys of the experiments.
  @param allowedVariantsByKey - The allowed variants by key. It is used to filter the local stored keys. It is necessary inform all the keys.
*/
const useGetExperimentVariant = ({
  userId,
  namespace,
  keys,
  allowedVariantsByKey
}: UseExperimentProps) => {
  const keysValueMapInLocalStorage = getKeysFromLocalStorage(keys)
  const { remainingKeys, filteredKeysValueMapInLocalStorage } =
    getRemainingKeysAndFilteredLocalStoredKeys(
      keys,
      keysValueMapInLocalStorage,
      allowedVariantsByKey
    )

  useInitializeExperimentsStore({
    keysValueMapInLocalStorage: filteredKeysValueMapInLocalStorage,
    remainingKeys
  })

  const { data, error, isSuccess, isFetched } = useQuery<
    GetExperimentVariantResponse,
    Error,
    Record<string, string>
  >({
    queryKey: makeExperimentKey({ userId, namespace, keys }),
    queryFn: () => getExperimentVariant({ userId, namespace, keys }),
    select: data => filterAndConvertExperiments(data.features),
    enabled: !!remainingKeys.length
  })
  const updateExperiments = useExperimentStore(state => state.updateExperiments)
  useEffect(() => {
    if (!!remainingKeys.length && isSuccess) {
      updateExperiments(
        convertToStoreExperimentsState({ experiments: data, isReady: true })
      )
      setKeysInLocalStorage(data)
    }
    // Should only run when the data is fetched
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFetched])

  useAddError({
    error,
    data: {
      context: Context.EXPERIMENT,
      code: ExperimentCode.GET_EXPERIMENT_VARIANT_ERROR,
      message: `Erro ao obter a variante do(s) experimento(s): ${namespace}-${keys.toString()} `
    }
  })
}

const useInitializeExperimentsStore = ({
  keysValueMapInLocalStorage,
  remainingKeys
}: {
  keysValueMapInLocalStorage: Record<string, string>
  remainingKeys: Array<string>
}) => {
  const updateExperiments = useExperimentStore(state => state.updateExperiments)

  useEffect(() => {
    const remainingKeysValueMap = Object.fromEntries(
      remainingKeys.map(key => [key, undefined])
    )
    updateExperiments({
      ...(Object.keys(keysValueMapInLocalStorage).length
        ? convertToStoreExperimentsState({
            experiments: keysValueMapInLocalStorage,
            isReady: true
          })
        : {}),
      ...(Object.keys(remainingKeysValueMap).length
        ? convertToStoreExperimentsState({
            experiments: remainingKeysValueMap,
            isReady: false
          })
        : {})
    })

    // We want to dispatch the event only once, when the component is mounted
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
}

const getRemainingKeysAndFilteredLocalStoredKeys = (
  allKeys: Array<string>,
  keysValueMapInLocalStorage: Record<string, string>,
  allowedVariantsByKey?: Record<string, Array<string>>
) => {
  let filteredKeysValueMapInLocalStorage: Record<string, string> = {}

  if (allowedVariantsByKey) {
    Object.entries(keysValueMapInLocalStorage).forEach(([key, value]) => {
      if (allowedVariantsByKey[key].includes(value)) {
        filteredKeysValueMapInLocalStorage[key] = value
      }
    })
  } else filteredKeysValueMapInLocalStorage = keysValueMapInLocalStorage

  const remainingKeys: Array<string> = []
  allKeys.forEach(key => {
    if (!Object.keys(filteredKeysValueMapInLocalStorage).includes(key)) {
      remainingKeys.push(key)
    }
  })

  return { remainingKeys, filteredKeysValueMapInLocalStorage }
}

const convertToObject = (experiments: { key: string; value: string }[]) => {
  const formattedExperiments: Record<string, string> = {}
  experiments.forEach(({ key, value }) => {
    formattedExperiments[key] = value
  })

  return formattedExperiments
}

const convertToStoreExperimentsState = ({
  experiments,
  isReady
}:
  | ConvertToReadyStoreExperimentsStateProps
  | ConvertToUndefinedStoreExperimentsStateProps) => {
  const formattedExperiments: Experiments = {}

  if (isReady)
    Object.entries(experiments).forEach(([key, value]) => {
      formattedExperiments[key] = { value, isReady }
    })
  else
    Object.entries(experiments).forEach(([key, value]) => {
      formattedExperiments[key] = { isReady, value }
    })

  return formattedExperiments
}

const filterAndConvertExperiments = (
  experiments: { key: string; value: string }[]
) => {
  const filteredExperiments = experiments.filter(
    ({ value }) => value !== 'default'
  )

  return convertToObject(filteredExperiments)
}

export type {
  ConvertToStoreExperimentsStateBaseProps,
  ConvertToReadyStoreExperimentsStateProps,
  ConvertToUndefinedStoreExperimentsStateProps,
  UseExperimentProps
}

export { useGetExperimentVariant }
